import {
  ClipEditSessionStore,
  type SafeZoneVariantSnapshot,
  type VideoClipPreview,
} from '@src/stores-v2/ClipEditSessionStore';
import { useClipPreview } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { getLastStoryLanguage } from '@src/utility/lastStoryLanguage';
import { useEffect, useRef } from 'react';

import type { SafeZoneSelection } from '@src/safe-zones/types';

/**
 * Creates and owns a `ClipEditSessionStore` for the lifetime of the
 * SocialClipShareModal. Deep-clones the clip prop on construction and
 * rebuilds the session when the clip reference changes (resource loading).
 */
export function useClipEditSession(
  clip: VideoClipPreview,
  // Read once at construction (PLAT-10948) to restore a shared/refreshed link's
  // language + preview mode from the URL. Later renders are ignored.
  initial?: {
    /**
     * `?clipLang=` when the link carries one. Wins over the per-clip "last
     * used" seed below (PLAT-11748) for the same reason the overlay's does — a
     * shared link must open on the sender's language. Absent ⇒ the language
     * this clip was last viewed in, else Original.
     */
    initialLanguage?: string | null;
    initialPreviewMode?: 'live' | 'rendered';
    /** `?clipPublishRecord=` — the exact publish to play (PLAT-11548). */
    initialPublishRecordId?: string | null;
    /**
     * Overlay restored from `?clipSafeZone=`. Wins over the singleton "last
     * used" seed below — a shared link must show the sender's overlay, not the
     * recipient's previous choice. Omitted (no param) ⇒ the singleton.
     */
    initialSafeZone?: SafeZoneSelection;
    initialSafeZoneVariants?: SafeZoneVariantSnapshot;
  },
): ClipEditSessionStore {
  const videoCreator = useVideoCreatorStore();
  const clipPreviewStore = useClipPreview();
  const defaultAr = videoCreator.getDefaultAspectRatio();

  // Stable ref — the store is created once and updated via replaceClip.
  const storeRef = useRef<ClipEditSessionStore | null>(null);

  if (!storeRef.current) {
    storeRef.current = new ClipEditSessionStore({
      clip,
      defaultAspectRatio: defaultAr,
      // Seed safe-zone state from the URL when a link carries one, else from the
      // singleton "last values" so the user's prior choice and variant toggles
      // survive modal close/reopen.
      initialSafeZone:
        initial?.initialSafeZone ?? clipPreviewStore.lastSafeZoneSelection,
      initialSafeZoneVariants:
        initial?.initialSafeZoneVariants ??
        clipPreviewStore.lastSafeZoneVariants,
      // PLAT-11898 — a clip never opened before seeds from the language the
      // user last worked in on the STORY, so a fresh clip on a story worked
      // in Japanese opens in Japanese. A clip already opened keeps its own,
      // Original included — hence the entry check, not `??` on its value. Not
      // checked against the translations here (the cache may not be loaded
      // yet); the bridge confirms it in the background.
      initialLanguage:
        initial?.initialLanguage ??
        (clip.id && clipPreviewStore.lastClipLanguages.has(clip.id)
          ? clipPreviewStore.getLastClipLanguage(clip.id)
          : getLastStoryLanguage(videoCreator.storyId)),
      initialPreviewMode: initial?.initialPreviewMode,
      initialPublishRecordId: initial?.initialPublishRecordId,
    });
  }

  // When the clip prop changes (e.g. resource loader populates videoSource),
  // update the session store rather than recreating it. This preserves
  // selectedDimension and previewMode across resource loading.
  useEffect(() => {
    if (storeRef.current) {
      storeRef.current.replaceClip(clip, defaultAr);
    }
  }, [clip]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      storeRef.current?.dispose();
    };
  }, []);

  return storeRef.current;
}
